import React, { useState } from 'react';
import { 
  Paper,
  Typography,
  Box,
  styled,
  Tabs,
  Tab
} from '@mui/material';
import GeneralProductsTab from './GeneralProductsTab';
import ProductDisplaySettings from './ProductDisplaySettings';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

// ECommercePage'den kopyalandı
const SectionTitle = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    '& .MuiSvgIcon-root': {
      marginRight: theme.spacing(1),
    }
}));

const StyledTabs = styled(Tabs)({
  minHeight: 'auto',
  '& .MuiTabs-indicator': {
    display: 'none',
  },
});

const StyledTab = styled((props: { label: string }) => (
    <Tab disableRipple {...props} />
  ))(({ theme }) => ({
    textTransform: 'none',
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: theme.typography.pxToRem(14),
    padding: theme.spacing(1, 3.5),
    minHeight: 'auto',
    minWidth: '120px',
    color: theme.palette.text.secondary,
    position: 'relative',
    backgroundColor: theme.palette.grey[200],
    clipPath: 'polygon(15% 0, 85% 0, 100% 100%, 0% 100%)',
    marginRight: theme.spacing(-2),

    '&:hover': {
        backgroundColor: theme.palette.grey[300],
    },
  
    '&.Mui-selected': {
      color: theme.palette.text.primary,
      fontWeight: theme.typography.fontWeightMedium,
      backgroundColor: theme.palette.background.paper,
      zIndex: 2,
    },
}));

// Tab paneli için yardımcı bileşen
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Bu arayüz ECommercePage.tsx içindeki EcommerceConfig'den alınmalı
interface ProductSettings {
    displayStyle: 'grid' | 'list';
    itemsPerRow: number;
    showRatings: boolean;
    enableQuickView: boolean;
    enableWishlist: boolean;
    hideOutOfStock: boolean;
    hideProductsWithoutImage: boolean;
}

interface ProductDisplaySettings {
    hideOutOfStock: boolean;
    hideProductsWithoutImage: boolean;
    productCardStyle?: any; // Eklendi
}

interface ProductSettingsProps {
  settings: {
      productSettings: ProductSettings;
      productDisplaySettings: ProductDisplaySettings;
  };
  onSettingsChange: {
      product: (setting: keyof ProductSettings, value: any) => void;
      display: (setting: keyof ProductDisplaySettings, value: any) => void;
  };
  companyId: string;
  configId: string;
}

const ProductSettingsComponent: React.FC<ProductSettingsProps> = ({ settings, onSettingsChange, companyId, configId }) => {
  const [activeTab, setActiveTab] = useState(0);
  const { t } = useTranslation('salesSystem');

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };
  
  const theme = useTheme();

  return (
    <Paper elevation={3} sx={{ position: 'relative', zIndex: 1 }}>
      <Box sx={{
          display: 'flex',
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider',
          pt: 1,
          px: 1,
          backgroundColor: theme.palette.grey[200],
      }}>
          <StyledTabs 
            value={activeTab} 
            onChange={handleTabChange} 
            aria-label={t('ecommerceProductsTabs.ariaLabel')}
            variant="scrollable"
            scrollButtons="auto"
          >
              <StyledTab label={t('ecommerceProductsTabs.generalProducts')} />
              <StyledTab label={t('ecommerceProductsTabs.productSettings')} />
          </StyledTabs>
      </Box>

      <TabPanel value={activeTab} index={0}>
          <GeneralProductsTab companyId={companyId} />
      </TabPanel>
      <TabPanel value={activeTab} index={1}>
        <ProductDisplaySettings settings={settings.productDisplaySettings} onSettingsChange={onSettingsChange.display} companyId={companyId} configId={configId} />
      </TabPanel>
    </Paper>
  );
};

export default ProductSettingsComponent; 